import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import StandardModal from '../modales/StandardModal';
import { useT, type TextKey } from '../../i18n';

/**
 * Estrellas de un préstamo (40 % de la calificación del cliente). Se abre al cerrar el préstamo con un abono,
 * o desde la hoja de vida para calificar o corregir.
 */
export interface RateLoanResult {
  data: { loanId: number; stars: number | null; score: number | null };
  client: { score: number | null; level: string; loans: number };
}

interface RateLoanDialogProps {
  open: boolean;
  loanId: number | null;
  clientName?: string | null;
  /** Al cerrar el préstamo con un abono se muestra «El préstamo quedó pagado…». */
  justClosed?: boolean;
  initialStars?: number | null;
  initialNote?: string | null;
  onClose: () => void;
  onSaved?: (result: RateLoanResult) => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

const RateLoanDialog: React.FC<RateLoanDialogProps> = ({
  open,
  loanId,
  clientName,
  justClosed = false,
  initialStars = null,
  initialNote = null,
  onClose,
  onSaved,
  showAlert,
}) => {
  const t = useT();
  const [stars, setStars] = useState<number | null>(initialStars);
  const [hover, setHover] = useState(-1);
  const [note, setNote] = useState(initialNote ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStars(initialStars);
      setNote(initialNote ?? '');
      setHover(-1);
    }
  }, [open, initialStars, initialNote]);

  const save = async () => {
    if (!loanId || !stars) return;
    setSaving(true);
    try {
      const { data } = await axios.post<RateLoanResult>(`loans/${loanId}/rating`, { stars, note });
      showAlert(t('rating.savedOk'), 'success');
      onSaved?.(data);
      onClose();
    } catch (error) {
      const code = axios.isAxiosError(error) ? (error.response?.data as { code?: string })?.code : undefined;
      showAlert(
        code === 'ALREADY_RATED' ? t('rating.alreadyRated') : code === 'INVALID_STARS' ? t('rating.invalidStars') : t('rating.saveError'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const shown = hover !== -1 ? hover : stars;

  return (
    <StandardModal
      open={open}
      onClose={() => !saving && onClose()}
      title={t('rating.dialogTitle')}
      subtitle={[clientName, loanId ? `#${loanId}` : null].filter(Boolean).join(' · ') || undefined}
      maxWidth={480}
      disableClose={saving}
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            {t('rating.later')}
          </Button>
          <Button variant="contained" onClick={() => void save()} disabled={saving || !stars}>
            {t('rating.save')}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {justClosed && (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t('rating.dialogClosed')}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {t('rating.dialogHelp')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Rating
            name="loan-rating"
            value={stars}
            precision={1}
            size="large"
            onChange={(_, v) => setStars(v)}
            onChangeActive={(_, v) => setHover(v)}
            sx={{ fontSize: { xs: '2.5rem', sm: '3rem' } }}
            aria-label={t('rating.starsLabel')}
          />
          <Typography variant="body2" color={shown ? 'text.primary' : 'text.secondary'} sx={{ minHeight: 20 }}>
            {shown ? t(`rating.star.${shown}` as TextKey) : t('rating.pickStars')}
          </Typography>
        </Box>
        <TextField
          label={t('rating.note')}
          placeholder={t('rating.notePlaceholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 300 }}
          multiline
          minRows={2}
          fullWidth
        />
      </Stack>
    </StandardModal>
  );
};

export default RateLoanDialog;
